import type { Bike } from "@/types/bike";

const SITE_URL = "https://frontrangecargobikes.com";

export type AiProvider = "chatgpt" | "claude";
export type AiPageSurface = "homepage" | "bike_detail";

export const AI_PROVIDERS: ReadonlyArray<{
  key: AiProvider;
  label: string;
}> = [
  { key: "chatgpt", label: "Ask ChatGPT" },
  { key: "claude", label: "Ask Claude" },
];

export function homepageAiPrompt(): string {
  return [
    "Help me prepare for a cargo-bike test ride at Front Range Cargo Bikes, a family-owned shop in Boulder, Colorado.",
    "First ask me about fit, who and what I need to carry, my usual routes and hills, trip distance, storage and access constraints, and the bikes or features I want to compare.",
    "Then explain what I should compare during a test ride, including handling with my normal load.",
    `Shop website: ${SITE_URL}/`,
    "Price and availability require confirmation with the shop.",
    "Do not submit a form or book anything for me.",
  ].join("\n\n");
}

function publishedSpecs(specs: Bike["specs"]): string {
  const entries = Object.entries(specs || {})
    .filter(([key, value]) => key.trim() && String(value).trim())
    .sort(([left], [right]) => left.localeCompare(right));

  if (entries.length === 0) return "No specifications are currently published on the product page.";
  return entries.map(([key, value]) => `${key}: ${value}`).join("; ");
}

export function bikeAiPrompt(bike: Pick<Bike, "handle" | "title" | "brand_display" | "specs">): string {
  const canonicalUrl = `${SITE_URL}/bikes/${encodeURIComponent(bike.handle)}`;

  return [
    "Help me evaluate this cargo bike for my family's use.",
    `Brand: ${bike.brand_display}`,
    `Exact model: ${bike.title}`,
    `Published specifications: ${publishedSpecs(bike.specs)}`,
    `Canonical product page: ${canonicalUrl}`,
    "First ask me about my intended family use: the children or gear I carry, my normal load, routes and hills, trip distance, storage and access constraints, and rider fit.",
    "Then explain what I should compare during a test ride.",
    "Price and availability require confirmation with the shop.",
    "Do not submit a form or book anything for me.",
  ].join("\n\n");
}

export function aiProviderUrl(provider: AiProvider, prompt: string): string {
  const encoded = encodeURIComponent(prompt);

  if (provider === "chatgpt") return `https://chatgpt.com/?q=${encoded}`;
  return `https://claude.ai/new?q=${encoded}`;
}
